import axios from 'axios';

// Importante: questo URL non deve essere intercettato dal service worker.
// Oggi src/service-worker.js registra strategie solo su `request.mode === 'navigate'`,
// immagini e script/style — `/api/*` (mode 'cors'/'same-origin', destination '') non
// matcha nessuna route, quindi passa al defaultHandler di Workbox (fetch nativa).
// Se in futuro si aggiunge una strategia che matcha `/api/*`, escludere `/api/ping/*`.
const PING_URL = '/api/ping/connectivity';
const PROBE_INTERVAL_MS = 2000;
const PROBE_JITTER_MS = 200;
const PROBE_TIMEOUT_MS = 3000;
const MAX_OUTAGE_DURATION_MS = 5 * 60 * 1000;

const CRITICAL_PATTERNS = [
    /^\/api\/items\/[^/]+$/,
    /^\/api\/products\/[^/]+\/locations$/,
    // /api/pickings/{idMacroPrel} (1 segmento, es. /bulk per reset selezioni)
    // e /api/pickings/{idMacroPrel}/{codDeposito} (2 segmenti, conferma pick
    // in picking-service.js:69 — la mutation più critica del flusso prelievo).
    /^\/api\/pickings\/[^/]+(\/[^/]+)?$/,
    /^\/api\/multi_pickings\/[^/]+\/(pause|resume|continue|bill)$/,
];

export const isPingUrl = (url) =>
    typeof url === 'string' && url.startsWith('/api/ping/');

let probeClient = null;
let currentOutage = null;
let probeTimer = null;
let probeAbort = null;
let maxDurationTimer = null;
let listenersBound = false;

function getProbeClient() {
    if (!probeClient) {
        probeClient = axios.create({ timeout: PROBE_TIMEOUT_MS });
    }
    return probeClient;
}

function isCritical(url) {
    return typeof url === 'string' && CRITICAL_PATTERNS.some((re) => re.test(url));
}

function getRequestType(config) {
    const method = config?.method?.toLowerCase();
    return method === 'get' ? 'query' : 'mutation';
}

function navigatorOnline() {
    return typeof navigator !== 'undefined' ? navigator.onLine : null;
}

function visibilityState() {
    return typeof document !== 'undefined' ? document.visibilityState : null;
}

function newOutageId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `outage-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function startOutage(error, config) {
    const id = newOutageId();
    const requestType = getRequestType(config);
    const criticalFlow = isCritical(config?.url);

    currentOutage = {
        id,
        startedAt: performance.now(),
        startedAtIso: new Date().toISOString(),
        firstError: {
            code: error?.code ?? null,
            message: error?.message ?? null,
            url: config?.url ?? null,
            method: config?.method ?? null,
        },
        navigatorOnlineStart: navigatorOnline(),
        visibilityStateStart: visibilityState(),
        failuresInWindow: 1,
        probeAttempts: 0,
        lastProbeAt: null,
        requestType,
        criticalFlow,
        maxDurationReached: false,
    };

    scheduleProbe();

    maxDurationTimer = setTimeout(() => {
        if (currentOutage && currentOutage.id === id) {
            currentOutage.maxDurationReached = true;
            stopProbeTimers();
        }
    }, MAX_OUTAGE_DURATION_MS);
}

function endOutage() {
    if (!currentOutage) return;
    currentOutage = null;
    stopProbeTimers();
}

function truncateOutage() {
    if (!currentOutage) return;
    currentOutage = null;
    stopProbeTimers();
}

function scheduleProbe() {
    if (probeTimer) clearTimeout(probeTimer);
    const jitter = (Math.random() * 2 - 1) * PROBE_JITTER_MS;
    probeTimer = setTimeout(runProbe, PROBE_INTERVAL_MS + jitter);
}

function runProbe() {
    const outage = currentOutage;
    if (!outage) return;

    const abort = new AbortController();
    probeAbort = abort;
    outage.probeAttempts += 1;
    outage.lastProbeAt = performance.now();

    getProbeClient()
        .get(PING_URL, { signal: abort.signal })
        .then(() => {
            if (currentOutage === outage) endOutage('probe');
        })
        .catch(() => {
            if (currentOutage === outage && !abort.signal.aborted) {
                scheduleProbe();
            }
        });
}

function stopProbeTimers() {
    if (probeTimer) {
        clearTimeout(probeTimer);
        probeTimer = null;
    }
    if (probeAbort) {
        probeAbort.abort();
        probeAbort = null;
    }
    if (maxDurationTimer) {
        clearTimeout(maxDurationTimer);
        maxDurationTimer = null;
    }
}

export const connectionMonitor = {
    recordFailure(error, config) {
        if (currentOutage) {
            currentOutage.failuresInWindow += 1;
            return;
        }
        startOutage(error, config);
    },

    recordSuccess() {
        if (currentOutage) {
            endOutage('user');
        }
    },

    start() {
        if (typeof window === 'undefined' || listenersBound) return;
        listenersBound = true;

        window.addEventListener('pagehide', () => truncateOutage('pagehide'));
        // visibilitychange è dispatchato su `document`, non su `window`. Su
        // alcuni browser bubblezza fino a window, ma non è garantito (WebView,
        // PWA con palmare in sleep). Usando document non lo perdiamo.
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                truncateOutage('hidden');
            }
        });
    },

    // Testing helpers — non usare in produzione.
    _reset() {
        stopProbeTimers();
        currentOutage = null;
        probeClient = null;
        listenersBound = false;
    },
    _getOutage() {
        return currentOutage ? { ...currentOutage } : null;
    },
    _setProbeClient(client) {
        probeClient = client;
    },
};
